/**
 * Shop card view-model (TRD-09 §6).
 *
 * Everything a card DECIDES lives here, free of React and Pixi, because the decisions are
 * the part worth testing: why a card is disabled, what the synergy preview line says, and
 * when it lights up green. The JSX in `ShopCard.tsx` is a dumb projection of this.
 *
 * Nothing in this file computes game state — `wouldMerge` and the deltas arrive on the
 * snapshot already computed by the sim (TRD-09 §2). This module only turns them into
 * words.
 */
import type { SynergyDelta, UiSnapshot } from '@prism/sim';

export type ShopSlotSnapshot = NonNullable<UiSnapshot['shop'][number]>;

/**
 * Why a card cannot be bought right now, or null if it can.
 *
 * Dimmed-with-a-reason, never hidden (R-09-08) — a silently unclickable button reads as
 * a bug. The two reasons are checked in the order the player can fix them: gold first,
 * because selling or waiting fixes gold, while a full bench needs a placement.
 */
export function disabledReason(
  slot: ShopSlotSnapshot,
  gold: number,
  benchFull: boolean,
): string | null {
  if (gold < slot.cost) return `Costs ${slot.cost}g — you have ${gold}g`;
  // A buy that completes a BENCH merge needs no slot — the sim accepts it on a full
  // bench (R-07-10a), so the card must not grey out the one purchase that helps.
  if (benchFull && !slot.mergesOnBench) return 'Bench is full — place or sell something first';
  return null;
}

/**
 * "Do I already have this one?" — answered on the card (playtest request).
 *
 * Returns null for an offer the run has never bought, so a brand-new tower stays visually
 * quiet and the badge means something when it does appear. ★1 copies and higher-star ones
 * are reported apart because only the ★1s can still merge with this purchase: a card
 * reading `OWNED ×2` when both are ★2s would promise a merge that cannot happen.
 */
export function ownedBadge(slot: ShopSlotSnapshot): string | null {
  if (slot.owned <= 0) return null;
  const merged = slot.owned - slot.ownedStarOne;
  if (slot.ownedStarOne > 0 && merged > 0) return `OWNED ×${slot.ownedStarOne} +${merged}★`;
  if (merged > 0) return `OWNED ${merged}★`;
  return `OWNED ×${slot.ownedStarOne}`;
}

export interface PreviewLine {
  classId: string;
  /** `+1 Frost → 4` — the count movement, always shown. */
  text: string;
  /** The tier summary this purchase unlocks, or null when no threshold is crossed. */
  unlocked: string | null;
  /** True exactly when a threshold is crossed — drives the green highlight (R-09-07). */
  crossesThreshold: boolean;
}

/**
 * One preview line per class on the tower (R-09-07).
 *
 * The count movement always shows, even when no threshold is crossed — `+1 Frost → 3`
 * with no unlock is how the player learns they are one short, which teaches the web just
 * as much as the unlock line does. The arrow target is the count reached, with the tier
 * appended only when it changes: the card must never claim "→ 4 ✦" when the fourth Frost
 * is still one buy away.
 */
export function previewLines(deltas: readonly SynergyDelta[]): PreviewLine[] {
  return deltas.map((d) => {
    const crosses = d.afterTier > d.beforeTier;
    return {
      classId: d.classId,
      text: crosses
        ? `+1 ${titleCase(d.classId)} → ${d.afterTier} ✦`
        : `+1 ${titleCase(d.classId)} → ${d.after}`,
      unlocked: d.unlocked,
      crossesThreshold: crosses,
    };
  });
}

/** `1.2s` / `0.15s` — attack interval reads better as seconds than as a rate at a glance. */
export function formatInterval(seconds: number): string {
  return `${trimZeros(seconds)}s`;
}

export function formatRange(tiles: number): string {
  return trimZeros(tiles);
}

function trimZeros(n: number): string {
  return String(Math.round(n * 100) / 100);
}

function titleCase(id: string): string {
  return id.charAt(0).toUpperCase() + id.slice(1);
}
